import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Ticket, Swords, Heart, Sparkles, Vote, PartyPopper, ArrowRight, RotateCcw, Crown } from "lucide-react";
import { confetti, floatEmojis, playScratch, playCoin, playFanfare } from "@/lib/celebrate";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/timeAgo";
import { SectionHead, PhoneShot } from "@/components/landing/Kit";

const Card = ({ icon: Icon, grad, title, text, children, testId, delay = 0 }) => (
  <div className="fade-up rounded-[2rem] bg-white border border-pink-100 p-5 sm:p-6 shadow-lg shadow-rose-100/60 flex flex-col gap-4 card-lift" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className={`w-11 h-11 shrink-0 rounded-2xl bg-gradient-to-br ${grad} flex items-center justify-center`}><Icon className="w-5 h-5 text-white" /></span>
      <div><h3 className="font-display text-lg font-extrabold leading-tight">{title}</h3><p className="text-xs text-slate-500">{text}</p></div>
    </div>
    {children}
  </div>
);

function DemoScratch() {
  const ref = useRef(null);
  const [won, setWon] = useState(false);
  const [round, setRound] = useState(0);
  const user = useRef(false);
  useEffect(() => {
    const c = ref.current, ctx = c.getContext("2d");
    const { width: w, height: h } = c.getBoundingClientRect();
    c.width = w * 2; c.height = h * 2; ctx.scale(2, 2);
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, "#d4d4d8"); g.addColorStop(0.5, "#f4f4f5"); g.addColorStop(1, "#a1a1aa");
    ctx.globalCompositeOperation = "source-over"; ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "#71717a"; ctx.font = "800 16px Outfit, sans-serif"; ctx.textAlign = "center"; ctx.fillText("PROBIER'S AUS ✨", w / 2, h / 2 + 6);
    c.style.opacity = "1"; setWon(false); user.current = false;
    let t = 0, raf;
    const auto = () => {
      if (user.current) return;
      t += 1;
      ctx.globalCompositeOperation = "destination-out";
      const x = (w / 2) + Math.sin(t / 9) * w * 0.42, y = (h / 2) + Math.cos(t / 5.3) * h * 0.38;
      ctx.beginPath(); ctx.arc(x, y, 16, 0, Math.PI * 2); ctx.fill();
      if (t < 260) raf = requestAnimationFrame(auto); else reveal();
    };
    const start = setTimeout(() => { raf = requestAnimationFrame(auto); }, 1200);
    return () => { clearTimeout(start); cancelAnimationFrame(raf); };
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps
  const reveal = () => {
    const c = ref.current; if (!c) return;
    c.style.transition = "opacity .5s"; c.style.opacity = "0"; setWon(true);
    if (user.current) { playFanfare(); const r = c.getBoundingClientRect(); confetti({ x: r.left + r.width / 2, y: r.top + r.height / 2, count: 80, spread: 1.3 }); }
  };
  const scratch = (e) => {
    if (won || (e.buttons !== 1 && e.pointerType === "mouse")) return;
    user.current = true;
    const c = ref.current, r = c.getBoundingClientRect(), ctx = c.getContext("2d");
    ctx.globalCompositeOperation = "destination-out"; ctx.beginPath(); ctx.arc(e.clientX - r.left, e.clientY - r.top, 20, 0, Math.PI * 2); ctx.fill();
    if (Math.random() < 0.3) playScratch();
  };
  const check = () => {
    if (won || !user.current) return;
    const c = ref.current, d = c.getContext("2d").getImageData(0, 0, c.width, c.height).data;
    let clear = 0; for (let i = 3; i < d.length; i += 64) if (d[i] === 0) clear++;
    if (clear / (d.length / 64) > 0.45) reveal();
  };
  return (
    <div className="space-y-3">
      <div className={`relative aspect-[16/9] rounded-3xl overflow-hidden border-4 border-amber-300 select-none touch-none ${won ? "win-glow" : ""}`} data-testid="landing-demo-scratch">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-200 via-yellow-100 to-pink-200 flex flex-col items-center justify-center">
          <p className="text-[11px] font-extrabold uppercase tracking-widest text-amber-700">Großer Gewinn!</p>
          <p className={`font-display text-5xl font-black text-rose-600 ${won ? "prize-pop" : ""}`}>+50</p>
          <p className="text-xs font-bold text-amber-700">Punkte</p>
        </div>
        <canvas ref={ref} className="absolute inset-0 w-full h-full cursor-grab" onPointerDown={scratch} onPointerMove={scratch} onPointerUp={check} onPointerLeave={check} />
      </div>
      <button type="button" onClick={() => setRound((r) => r + 1)} data-testid="landing-demo-scratch-again" className={`w-full h-10 rounded-full bg-amber-50 text-amber-700 text-sm font-bold inline-flex items-center justify-center gap-2 transition-opacity ${won ? "opacity-100" : "opacity-0 pointer-events-none"}`}><RotateCcw className="w-4 h-4" /> Nochmal rubbeln</button>
    </div>
  );
}

const PLAYERS = [["Lena", "from-rose-400 to-pink-500"], ["Max", "from-violet-500 to-fuchsia-500"]];

function DemoDuel() {
  const [score, setScore] = useState([12, 9]);
  const [fans, setFans] = useState([14, 11]);
  useEffect(() => {
    const iv = setInterval(() => setScore(([a, b]) => (a + b > 160 ? [12, 9] : [a + Math.ceil(Math.random() * 6), b + Math.ceil(Math.random() * 6)])), 1100);
    return () => clearInterval(iv);
  }, []);
  const cheer = (i, e) => { floatEmojis(e.currentTarget, "❤️", 8); playCoin(2); setFans((f) => f.map((v, j) => (j === i ? v + 1 : v))); };
  const max = Math.max(...score, 1);
  return (
    <div className="rounded-3xl bg-gradient-to-b from-violet-950 to-rose-950 p-5 text-white" data-testid="landing-demo-duel">
      <div className="flex items-start justify-between gap-2">
        {PLAYERS.map(([n, g], i) => (
          <div key={n} className={`flex flex-col items-center gap-2 w-24 ${i ? "vs-in-right order-3" : "vs-in-left order-1"}`}>
            <span className={`w-16 h-16 rounded-full bg-gradient-to-br ${g} ring-4 ring-white/80 flex items-center justify-center font-display text-2xl font-black`}>{n[0]}</span>
            <b className="text-sm">{n}</b>
            <button type="button" onClick={(e) => cheer(i, e)} data-testid={`landing-demo-cheer-${i}`} className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white/15 hover:bg-white/25 text-sm font-extrabold active:scale-90 transition-transform">
              <Heart className="w-4 h-4 fill-rose-400 text-rose-400" /> {fans[i]}
            </button>
          </div>
        ))}
        <span className="order-2 vs-clash relative mt-4 w-12 h-12 shrink-0 rounded-full bg-gradient-to-br from-amber-300 to-pink-500 flex items-center justify-center"><Swords className="w-6 h-6" /><span className="vs-ring" aria-hidden /></span>
      </div>
      <div className="mt-4 space-y-2">
        {PLAYERS.map(([n, g], i) => (
          <div key={n} className="flex items-center gap-2 text-xs font-bold">
            <span className="w-10">{n}</span>
            <span className="flex-1 h-3 rounded-full bg-white/10 overflow-hidden"><span className={`block h-full rounded-full bg-gradient-to-r ${g} transition-[width] duration-700`} style={{ width: `${(score[i] / max) * 100}%` }} /></span>
            <span className="w-8 text-right tabular-nums">{score[i]}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-[11px] text-pink-200">Tippe aufs Herz und feuer deinen Favoriten an!</p>
    </div>
  );
}

const WHEEL = ["#F43F5E", "#FBBF24", "#EC4899", "#A78BFA", "#FB7185", "#F59E0B", "#D946EF", "#FDA4AF"];

function DemoWheel() {
  const [rot, setRot] = useState(0);
  const spin = () => { setRot((r) => r + 1440 + Math.random() * 360); playCoin(1); setTimeout(() => playCoin(4), 2600); };
  return (
    <button type="button" onClick={spin} data-testid="landing-demo-wheel" className="relative mx-auto block w-40 h-40" aria-label="Glücksrad drehen">
      <span className="absolute inset-0 rounded-full shadow-xl shadow-rose-200 border-4 border-white" style={{ background: `conic-gradient(${WHEEL.map((c, i) => `${c} ${i * 45}deg ${(i + 1) * 45}deg`).join(",")})`, transform: `rotate(${rot}deg)`, transition: "transform 2.8s cubic-bezier(.15,.85,.25,1)" }} />
      <span className="absolute inset-[38%] rounded-full bg-white shadow flex items-center justify-center text-[10px] font-black text-rose-600">DREH!</span>
      <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-[14px] border-t-slate-800" />
    </button>
  );
}

const IDEAS = [["Tagesquiz mit Extra-Punkten", 42], ["Live-Treffen mit euch", 35], ["Mehr Geschenke", 23]];

function DemoIdeas() {
  const [votes, setVotes] = useState(IDEAS.map((i) => i[1]));
  const [mine, setMine] = useState(null);
  const total = votes.reduce((a, b) => a + b, 0);
  const vote = (i, e) => { if (mine !== null) return; floatEmojis(e.currentTarget, "💖", 6); setMine(i); setVotes((v) => v.map((x, j) => (j === i ? x + 1 : x))); };
  return (
    <ul className="space-y-2" data-testid="landing-demo-ideas">
      {IDEAS.map(([t], i) => (
        <li key={t}>
          <button type="button" onClick={(e) => vote(i, e)} data-testid={`landing-demo-idea-${i}`} className={`relative w-full overflow-hidden rounded-2xl border px-4 py-2.5 text-left text-sm font-semibold ${mine === i ? "border-rose-400" : "border-pink-100"}`}>
            <span className="absolute inset-y-0 left-0 bg-rose-100/80 transition-[width] duration-700" style={{ width: `${Math.round((votes[i] / total) * 100)}%` }} />
            <span className="relative flex justify-between gap-2"><span>{t}</span><span className="text-xs text-slate-500 tabular-nums">{Math.round((votes[i] / total) * 100)} %</span></span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export const LIVE = [
  [Crown, "Sophie hat den Glücksrad-Jackpot geknackt!", "from-amber-300 to-orange-500"],
  [Swords, "Lena und Max haben ein Punkte-Duell gestartet", "from-violet-500 to-pink-500"],
  [Ticket, "Jonas hat 100 Punkte freigerubbelt 🤩", "from-amber-300 to-pink-500"],
  [PartyPopper, "Mia hat ihr Geschenk eingelöst 🎁", "from-rose-400 to-fuchsia-500"],
];

export const KIND_STYLE = { jackpot: [Crown, "from-amber-300 to-orange-500"], duel: [Swords, "from-violet-500 to-pink-500"], scratch: [Ticket, "from-amber-300 to-pink-500"] };

export const GamesShowcase = () => (
  <section id="spiele" className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 scroll-mt-20" data-testid="landing-games">
    <div className="grid lg:grid-cols-[300px_1fr] gap-10 items-center">
      <PhoneShot src="/screens/spiele.jpg" alt="Die Spiele-Seite in der App" tilt={-2} testId="landing-phone-games" chips={[{ icon: Ticket, title: "+100 Punkte", sub: "freigerubbelt" }, { icon: Swords, title: "Duell gewonnen!", sub: "gegen Tom" }]} />
      <SectionHead eyebrow="Neu: Spiele & Live-Momente" title="Spielen, anfeuern," accent="gemeinsam gewinnen" lead="Probier es direkt hier aus – rubbel das Los frei, feuer im Duell an oder dreh am Glücksrad. In der Community gibt's dafür echte Punkte." />
    </div>
    <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      <Card icon={Ticket} grad="from-amber-300 to-pink-500" title="Rubbellose" text="Bis zu 100 Punkte freirubbeln" testId="landing-games-scratch"><DemoScratch /></Card>
      <Card icon={Swords} grad="from-violet-500 to-pink-500" title="Freundes-Duelle" text="Wer in 24 Stunden mehr sammelt, gewinnt alles" testId="landing-games-duel" delay={120}><DemoDuel /></Card>
      <Card icon={Sparkles} grad="from-rose-400 to-amber-400" title="Tägliches Glücksrad" text="Jeden Tag gratis drehen – mit Jackpot" testId="landing-games-wheel" delay={240}><DemoWheel /><p className="text-center text-xs text-slate-400">Tippe aufs Rad 🎡</p></Card>
      <Card icon={Vote} grad="from-fuchsia-500 to-rose-500" title="Ideen-Abstimmung" text="Bestimme mit, was als Nächstes kommt" testId="landing-games-ideas" delay={240}><DemoIdeas /></Card>
      <div className="fade-up rounded-[2rem] pink-gradient p-6 text-white flex flex-col justify-between gap-5 shadow-xl shadow-rose-300/50" style={{ animationDelay: "360ms" }}>
        <div><p className="font-display text-2xl font-extrabold leading-tight">Bereit für die echte Runde?</p><p className="text-sm text-white/85 mt-1">Kostenlos Mitglied werden, Punkte sammeln und gegen tolle Geschenke eintauschen.</p></div>
        <Link to="/registrieren" data-testid="landing-games-cta" className="inline-flex items-center justify-center gap-2 h-12 rounded-full bg-white text-rose-600 font-extrabold text-sm hover:scale-[1.03] transition-transform wheel-cta">Jetzt mitspielen <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  </section>
);
